import { combineReducers } from 'redux';
import { delay } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import moment from 'moment';
import isNil from 'lodash.isnil';
import isEqual from 'lodash.isequal';
import findKey from 'lodash.findkey';
import partial from 'lodash.partial';
import { kmToMile } from '../utils/convertUnits';
import prepareBody from '../utils/prepareBody';
import { getSession } from '../utils/storage';
import isSet from '../utils/isSet';
import { CLOSE_PLANS_MODAL } from './landing';
import signupValidator from '../utils/signupValidator';
import signupRaceValidator from '../utils/signupRaceValidator';
import takeLatestFilter from '../utils/takeLatestFilter';
import { terrains } from '../constants';
import { selectThemeId } from './theme';
import { selectRunnerId, LOGIN_SUCCESS, loginAction, RESET_LEGACY_RUNNER } from './auth';
import calculatePace from '../utils/calculatePace';
import paceStringToMinutes from '../utils/paceStringToMinutes';

// ---------
// CONSTANTS
// ---------
export const CHANGE_FIELD = 'signup/CHANGE_FIELD';
export const CHANGE_ALL_FIELDS = 'signup/CHANGE_ALL_FIELDS';
export const ADD_RACE = 'signup/ADD_RACE';
export const CHANGE_RACE = 'signup/CHANGE_RACE';
export const REMOVE_RACE = 'signup/REMOVE_RACE';
export const FETCH_LEVELS = 'signup/FETCH_LEVELS';
export const FETCH_LEVELS_SUCCESS = 'signup/FETCH_LEVELS_SUCCESS';
export const FETCH_LEVELS_FAILURE = 'signup/FETCH_LEVELS_FAILURE';
export const FETCH_NYRR_RACES = 'signup/FETCH_NYRR_RACES';
export const FETCH_NYRR_RACES_SUCCESS = 'signup/FETCH_NYRR_RACES_SUCCESS';
export const FETCH_NYRR_RACES_FAILURE = 'signup/FETCH_NYRR_FAILURE';
export const FETCH_CHARITIES = 'signup/FETCH_CHARITIES';
export const FETCH_CHARITIES_SUCCESS = 'signup/FETCH_CHARITIES_SUCCESS';
export const FETCH_CHARITIES_FAILURE = 'signup/FETCH_CHARITIES_FAILURE';
export const CHECK_CHARITY_CODE = 'signup/CHECK_CHARITY_CODE';
export const CHECK_CHARITY_CODE_SUCCESS = 'signup/CHECK_CHARITY_CODE_SUCCESS';
export const CHECK_CHARITY_CODE_FAILURE = 'signup/CHECK_CHARITY_CODE_FAILURE';
export const CHANGE_CHARITY_CODE = 'signup/CHANGE_CHARITY_CODE';
export const GET_ACTIVE_PLAN = 'signup/GET_ACTIVE_PLAN';
export const GET_ACTIVE_PLAN_SUCCESS = 'signup/GET_ACTIVE_PLAN_SUCCESS';
export const GET_ACTIVE_PLAN_FAILURE = 'signup/GET_ACTIVE_PLAN_FAILURE';
export const STRIPE_CHARGE = 'signup/STRIPE_CHARGE';
export const STRIPE_CHARGE_SUCCESS = 'signup/STRIPE_CHARGE_SUCCESS';
export const STRIPE_CHARGE_FAILURE = 'signup/STRIPE_CHARGE_FAILURE';
export const STRIPE_CHECKOUT = 'signup/STRIPE_CHECKOUT';
export const STRIPE_CHECKOUT_SUCCESS = 'signup/STRIPE_CHECKOUT_SUCCESS';
export const STRIPE_CHECKOUT_FAILURE = 'signup/STRIPE_CHECKOUT_FAILURE';
export const STRIPE_CHARGE_GC = 'signup/STRIPE_CHARGE_GC';
export const STRIPE_CHARGE_GC_SUCCESS = 'signup/STRIPE_CHARGE_SUCCESS_GC';
export const STRIPE_CHARGE_GC_FAILURE = 'signup/STRIPE_CHARGE_FAILURE_GC';
export const CHECK_PAYMENT = 'signup/CHECK_PAYMENT';
export const CHECK_PAYMENT_SUCCESS = 'signup/CHECK_PAYMENT_SUCCESS';
export const CHECK_PAYMENT_FAILURE = 'signup/CHECK_PAYMENT_FAILURE';
export const FREE_CHARGE = 'signup/FREE_CHARGE';
export const FREE_CHARGE_SUCCESS = 'signup/FREE_CHARGE_SUCCESS';
export const FREE_CHARGE_FAILURE = 'signup/FREE_CHARGE_FAILURE';
export const SIGNUP = 'signup/SIGNUP';
export const SIGNUP_SUCCESS = 'signup/SIGNUP_SUCCESS';
export const SIGNUP_FAILURE = 'signup/SIGNUP_FAILURE';
export const SET_ERROR = 'signup/SET_ERROR';
export const CLEAR_ERROR = 'signup/CLEAR_ERROR';
export const SET_RACE_ERROR = 'signup/SET_RACE_ERROR';
export const OPEN_IS_YOU_MODAL = 'signup/OPEN_IS_YOU_MODAL';
export const CLOSE_IS_YOU_MODAL = 'signup/CLOSE_IS_YOU_MODAL';
export const GET_SIGNUP_LOGGED_IN = 'signup/GET_SIGNUP_LOGGED_IN';
export const GET_SIGNUP_LOGGED_IN_SUCCESS = 'signup/GET_SIGNUP_LOGGED_IN_SUCCESS';
export const GET_SIGNUP_LOGGED_IN_FAILURE = '/signup/GET_SIGNUP_LOGGED_IN_FAILURE';
export const SET_PLAN_ID = 'signup/SET_PLAN_ID';
export const SET_RUNNER_ID = 'signup/SET_RUNNER_ID';
export const FETCH_PADDLE_PRODUCTS = 'signup/FETCH_PADDLE_PRODUCTS';
export const FETCH_PADDLE_PRODUCTS_SUCCESS = 'signup/FETCH_PADDLE_PRODUCTS_SUCCESS';
export const FETCH_PADDLE_PRODUCTS_FAILURE = 'signup/FETCH_PADDLE_PRODUCTS_FAILURE';
export const FETCH_PADDLE_PRICES = 'signup/FETCH_PADDLE_PRICES';
export const FETCH_PADDLE_PRICES_SUCCESS = 'signup/FETCH_PADDLE_PRICES_SUCCESS';
export const FETCH_PADDLE_PRICES_FAILURE = 'signup/FETCH_PADDLE_PRICES_FAILURE';
export const CONFIRM_PLAN_PURCHASE = 'signup/CONFIRM_PLAN_PURCHASE';
export const CONFIRM_PLAN_PURCHASE_SUCCESS = 'signup/CONFIRM_PLAN_PURCHASE_SUCCESS';
export const CONFIRM_PLAN_PURCHASE_FAILURE = 'signup/CONFIRM_PLAN_PURCHASE_FAILURE';
export const CLOSE_PADDLE = 'signup/CLOSE_PADDLE';
export const OPEN_PADDLE = 'signup/OPEN_PADDLE';
export const RETRY_CONFIRM_PURCHASE = 'signup/RETRY_CONFIRM_PURCHASE';
export const RETRY_CONFIRM_PURCHASE_FAILURE = 'signup/RETRY_CONFIRM_PURCHASE_FAILURE';
export const OPEN_ACTIVE_PLAN_ALERT = 'signup/OPEN_ACTIVE_PLAN_ALERT';
export const CLOSE_ACTIVE_PLAN_ALERT = 'signup/CLOSE_ACTIVE_PLAN_ALERT';
export const PROCESS_ORDER_FAILED = 'signup/PROCESS_ORDER_FAILED';
export const SHOW_PURCHASE_SPINNER = 'signup/SHOW_PURCHASE_SPINNER';
export const RESET_FILLING_ORDER = 'signup/RESET_FILLING_ORDER';

// --------
// REDUCERS
// --------
export function fieldsReducer(state = {
    charityName: 'No',
    runday: 3,
}, action) {
    switch (action.type) {
        case CLOSE_PLANS_MODAL:
            return {
                ...state,
                distance: action.plan,
            };
        case CHANGE_FIELD:
            return {
                ...state,
                [action.key]: action.val,
            };
        case CHANGE_ALL_FIELDS:
            return {
                ...state,
                ...action.race,
            };
        case FETCH_NYRR_RACES_SUCCESS:
            if (action.paramsRace === 'other') {
                return {
                    ...state,
                    nyrrRaceName: 'Other',
                };
            }
            return state;
        case SIGNUP:
            return {
                ...state,
                mgoal: action.mgoal,
            };
        case GET_ACTIVE_PLAN_SUCCESS: {
            if (action.email !== state.email) {
                return state;
            }
            const demographics = {};
            const result = action.result.Demographics;
            if (result) {
                if (result.Gender) {
                    demographics.gender = result.Gender;
                }
                if (result.Motivation) {
                    demographics.motivation = result.Motivation;
                }
                if (!isNil(result['Runner Level'])) {
                    demographics.knowledge = result['Runner Level'];
                }
                if (result.Gender) {
                    demographics.gender = result.Gender;
                }
                if (result.Units) {
                    demographics.units = result.Units.toLowerCase();
                }
                if (result.Weight) {
                    demographics.weight = result.Weight;
                }
                if (result.Height) {
                    demographics.height = result.Height;
                }
                if (result['Zip Code']) {
                    demographics.zipcode = result['Zip Code'];
                }
                if (result.Name) {
                    demographics.name = result.Name;
                }
                if (result.ProfilePic) {
                    demographics.avatar = result.ProfilePic;
                }
            }
            return {
                ...state,
                ...demographics,
                activePlan: action.result['Active Plan'] === 'Yes',
                planOver: action.result['Plan Over'] === 'Yes',
            };
        }
        case GET_SIGNUP_LOGGED_IN_SUCCESS: {
            if (action.email !== state.email) {
                return state;
            }
            const demographics = action.result.Demographics;
            const result = action.result.Demographics;
            if (result) {
                if (result.sex) {
                    demographics.gender = result.sex;
                    delete demographics.sex;
                }
                if (result.units) {
                    demographics.units = result.units.toLowerCase();
                }
                if (result.ProfilePic) {
                    demographics.avatar = result.ProfilePic;
                }
            }
            const training = action.result['Recent Training'];
            const trainingResult = action.result['Recent Training'];
            if (trainingResult) {
                if (trainingResult.runpace) {
                    training.runpace_string = trainingResult.runpace;
                    training.runpace = paceStringToMinutes(trainingResult.runpace);
                }
                if (trainingResult.m_experience) {
                    training.m_experience = trainingResult.m_experience === 'yes';
                }
                if (trainingResult.hm_experience) {
                    training.hm_experience = trainingResult.hm_experience === 'yes';
                }
            }
            return {
                ...state,
                ...demographics,
                ...training,
                runRace: isSet(action.result['Recent Races']),
            };
        }
        case CHECK_PAYMENT_SUCCESS:
            return {
                ...state,
                email: getSession('signup.fields.email'),
                password: getSession('signup.fields.password'),
            };
        case RESET_LEGACY_RUNNER:
            return {
                ...state,
                email: null,
                password: null,
            };
        default:
            return state;
    }
}

export function racesReducer(state = [{
    name: '',
    date: null,
    distance: null,
    hours: '',
    minutes: '',
    seconds: '',
}], action) {
    switch (action.type) {
        case ADD_RACE:
            return [
                ...state, {
                    name: '',
                    date: null,
                    distance: null,
                    hours: '',
                    minutes: '',
                    seconds: '',
                },
            ];
        case CHANGE_RACE:
            return [
                ...state.slice(0, action.index),
                {
                    ...state[action.index],
                    [action.key]: action.value,
                },
                ...state.slice(action.index + 1),
            ];
        case REMOVE_RACE:
            return [
                ...state.slice(0, action.index),
                ...state.slice(action.index + 1),
            ];
        case GET_SIGNUP_LOGGED_IN_SUCCESS: {
            const races = action.result['Recent Races'];
            if (races) {
                return Object.keys(races).reduce((acc, raceKey) => {
                    const currRace = races[raceKey];
                    const dateObj = moment(currRace.date).toDate();
                    currRace.date = dateObj;
                    acc.push(currRace);
                    return acc;
                }, []);
            }
            return state;
        }
        default:
            return state;
    }
}

export function raceErrorsReducer(state = [{}], action) {
    switch (action.type) {
        case ADD_RACE:
            return [
                ...state,
                {},
            ];
        case CHANGE_RACE:
            return [
                ...state.slice(0, action.index), {
                    ...state[action.index],
                    // reset keys
                    [action.key]: null,
                },
                ...state.slice(action.index + 1),
            ];
        case REMOVE_RACE:
            return [
                ...state.slice(0, action.index),
                ...state.slice(action.index + 1),
            ];
        case SET_RACE_ERROR:
            return [
                ...state.slice(0, action.index),
                {
                    ...state[action.index],
                    [action.key]: action.error,
                },
                ...state.slice(action.index + 1),
            ];
        case GET_SIGNUP_LOGGED_IN_SUCCESS: {
            const races = action.result['Recent Races'];
            if (races) {
                return Object.keys(races).reduce((acc) => {
                    acc.push({});
                    return acc;
                }, []);
            }
            return state;
        }
        default:
            return state;
    }
}

export function errorsReducer(state = {}, action) {
    switch (action.type) {
        case CLEAR_ERROR: {
            return {
                ...state,
                [action.key]: null,
            };
        }
        case LOGIN_SUCCESS: {
            return {};
        }
        case SET_ERROR:
            return {
                ...state,
                [action.key]: action.error,
            };
        default:
            return state;
    }
}

export function levelsReducer(state = {
    loading: null,
    items: null,
}, action) {
    switch (action.type) {
        case CHANGE_FIELD:
        case CHANGE_RACE:
            return {
                loading: null,
                items: null,
            };
        case FETCH_LEVELS:
            return {
                ...state,
                loading: true,
                error: null,
                items: [],
            };
        case FETCH_LEVELS_SUCCESS:
            return {
                ...state,
                loading: false,
                error: null,
                items: action.result ? action.result.map((plan) => {
                    const times = [plan['time low'], plan['time high']];
                    action.result.forEach((p) => {
                        if (plan.id !== p.id) {
                            times.push(p['time low']);
                        }
                    });
                    return {
                        ...plan,
                        times: times.sort(),
                    };
                }) : null,
            };
        case FETCH_LEVELS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error,
                items: [],
            };
        default:
            return state;
    }
}

export const nyrrRacesReducer = (state = {
    error: null,
    items: [],
}, action) => {
    switch (action.type) {
        case FETCH_NYRR_RACES_SUCCESS:
            return {
                ...state,
                items: action.result,
                error: null,
            };
        case FETCH_NYRR_RACES_FAILURE:
            return {
                ...state,
                error: action.error,
            };
        case FETCH_NYRR_RACES:
        default:
            return state;

    }
};

export const charitiesReducer = ((state = {
    error: null,
    charities: ['No'],
}, action) => {
    switch (action.type) {
        case FETCH_CHARITIES_SUCCESS:
            return {
                error: null,
                charities: ['No', ...action.result],
            };
        case FETCH_CHARITIES_FAILURE:
            return {
                ...state,
                error: action.error,
            };
        case FETCH_CHARITIES:
        default:
            return state;
    }
});

export const charityCodeReducer = ((state = {
    code: '',
    valid: false,
    error: null,
}, action) => {
    switch (action.type) {
        case CHANGE_CHARITY_CODE:
            return {
                code: action.val,
                valid: false,
                error: null,
            };
        case CHECK_CHARITY_CODE_SUCCESS:
            return {
                ...state,
                valid: !!action.result.success,
                error: action.result.error,
                errorMsg: action.result.error_msg,
            };
        case CHECK_CHARITY_CODE_FAILURE:
            return {
                ...state,
                valid: false,
                error: action.result.error,
                errorMsg: action.result.error_msg,
            };
        case CHECK_CHARITY_CODE:
        default:
            return state;
    }
});

export const creatingPlanInProgress = (state = false, action) => {
    switch (action.type) {
        case SIGNUP:
            return true;
        case SIGNUP_SUCCESS:
        case SIGNUP_FAILURE:
            return false;
        default:
            return state;
    }
};

export function isYouModalOpened(state = false, action) {
    switch (action.type) {
        case OPEN_IS_YOU_MODAL:
            return true;
        case CLOSE_IS_YOU_MODAL:
            return false;
        default:
            return state;
    }
}

export function planIdReducer(state = null, action) {
    switch (action.type) {
        case SIGNUP_SUCCESS:
            return action.result.planid;
        case SET_PLAN_ID:
            return action.planId;
        default:
            return state;
    }
}

export function runnerIdReducer(state = null, action) {
    switch (action.type) {
        case SIGNUP_SUCCESS:
            return action.result.runnerid;
        case SET_RUNNER_ID:
            return action.runnerId;
        default:
            return state;
    }
}

export function products(state = {}, action) {
    switch (action.type) {
        case FETCH_PADDLE_PRODUCTS_SUCCESS:
            return {
                ...state,
                ...action.result,
            };
        default:
            return state;
    }
}

export function isActivePlanModalOpened(state = false, action) {
    switch (action.type) {
        case OPEN_ACTIVE_PLAN_ALERT:
            return true;
        case CLOSE_ACTIVE_PLAN_ALERT:
            return false;
        default:
            return state;
    }
}

export function stripeReducer(state = { checkoutLoading: false, freeChargeDone: false }, action) {
    switch (action.type) {
        case SIGNUP:
            return {};
        case STRIPE_CHARGE:
        case STRIPE_CHARGE_GC:
            return {
                loading: true,
            };
        case STRIPE_CHARGE_FAILURE:
        case STRIPE_CHARGE_GC_FAILURE:
        case FREE_CHARGE_FAILURE:
            return {
                error: action.error || 'Something went wrong, please try again.',
            };
        case STRIPE_CHARGE_SUCCESS:
        case STRIPE_CHARGE_GC_SUCCESS:
            return {
                done: true,
            };
        case STRIPE_CHECKOUT:
        case FREE_CHARGE:
            return {
                ...state,
                checkoutLoading: true,
            };
        case STRIPE_CHECKOUT_SUCCESS:
            return {
                ...state,
                checkoutLoading: false,
            };
        case FREE_CHARGE_SUCCESS:
            return {
                ...state,
                checkoutLoading: false,
                freeChargeDone: true,
            };
        default:
            return state;
    }
}


export function purchase(state = {}, action) {
    switch (action.type) {
        case CONFIRM_PLAN_PURCHASE:
        case FREE_CHARGE:
            return {
                ...state,
                fillingOrder: true,
            };
        case CONFIRM_PLAN_PURCHASE_SUCCESS:
            return {
                ...state,
                fillingOrder: false,
                orderFilled: true,
                webhookHit: action.result.webhookHit,
            };
        case CONFIRM_PLAN_PURCHASE_FAILURE:
            return {
                ...state,
                fillingOrder: false,
                orderFilled: false,
                webhookHit: action.result ? action.result.webhookHit : 0,
            };
        case RETRY_CONFIRM_PURCHASE:
            return {
                ...state,
                retryPurchaseTried: true,
            };
        case RETRY_CONFIRM_PURCHASE_FAILURE:
            return {
                ...state,
                error: action.error || 'Something went wrong, please try again.',
            };
        case CLOSE_PADDLE:
            return {
                ...state,
                paddleOpen: false,
            };
        case OPEN_PADDLE:
            return {
                ...state,
                paddleOpen: true,
                retryPurchaseTried: false,
            };
        case FREE_CHARGE_SUCCESS:
            return {
                ...state,
                fillingOrder: false,
                orderFilled: true,
                freeChargeDone: true,
            };
        case FREE_CHARGE_FAILURE:
            return {
                ...state,
                fillingOrder: false,
                orderFilled: false,
                freeChargeDone: true,
                error: action.error || 'Something went wrong, please try again.',
            };
        case PROCESS_ORDER_FAILED:
            return {
                ...state,
                fillingOrder: false,
                orderFilled: false,
                purchaseFailed: true,
            };
        case SHOW_PURCHASE_SPINNER:
            return {
                ...state,
                fillingOrder: true,
            };
        case RESET_FILLING_ORDER:
            return {
                ...state,
                fillingOrder: false,
            };
        default:
            return state;
    }
}

// ---------
// MAIN
// ---------
export default combineReducers({
    fields: fieldsReducer,
    races: racesReducer,
    nyrrRaces: nyrrRacesReducer,
    charities: charitiesReducer,
    charityCode: charityCodeReducer,
    levels: levelsReducer,
    errors: errorsReducer,
    raceErrors: raceErrorsReducer,
    stripe: stripeReducer,
    creatingPlanInProgress,
    planId: planIdReducer,
    runnerId: runnerIdReducer,
    isYouModalOpened,
    products,
    purchase,
    isActivePlanModalOpened,
});


// when key change check if
// other have error now
const errorDependent = {
    units: ['lrweek', 'milesweek', 'runday'],
    runday: ['milesweek', 'lrweek'],
    lrweek: ['milesweek'],
    milesweek: ['lrweek', 'runday'],
};

// -----
// SAGAS
// -----
export function* setErrorListener({ val, key }) {
    const { signup: { fields, errors } } = yield select();
    const authRunnerId = yield select(selectRunnerId);
    const fieldError = signupValidator(val, key, fields, authRunnerId);
    const newErrors = {};
    if (fieldError) {
        newErrors[key] = fieldError;
    }

    // some fields can be depended on
    // other fields for validation
    if (errorDependent[key]) {
        errorDependent[key].forEach((field) => {
            const otherError = signupValidator(fields[field], field, fields, authRunnerId);
            if (otherError) {
                newErrors[field] = otherError;
            }
        });
    }

    // clear errors
    yield [
        ...(errorDependent[key] || []),
        key,
    ].map(function* clearErrors(clear) {
        // clear error if not available anymore
        if (!newErrors[clear] && errors[clear]) {
            return yield put(clearError(clear));
        }
        // change error if available
        if (newErrors[clear] && errors[clear]) {
            return yield put(setError({
                key: clear,
                error: newErrors[clear],
            }));
        }
        return null;
    });

    if (Object.keys(newErrors).length > 0) {
        yield call(delay, 2000);
        yield Object.keys(newErrors).map(errorKey =>
            put(setError({
                key: errorKey,
                error: newErrors[errorKey],
            })),
        );
    }
}

export function* setRaceErrorListener({ index, value, key }) {
    if (key === 'hours' || key === 'minutes' || key === 'seconds') {
        yield put(setRaceError({ index, key: 'timeError', error: null }));
    }
    const { signup: { races } } = yield select();

    const hours = races[index].hours;
    const minutes = races[index].minutes;
    const seconds = races[index].seconds;
    const distance = races[index].distance;

    const hasDataForPace = !!(distance && (hours || minutes || seconds));

    const pace = hasDataForPace && calculatePace({
        hours,
        minutes,
        seconds,
        distance: (distance * 0.000621371192),
        wantTime: false,
    });

    const tooSlow = pace && pace > 1200;
    const toFast = pace && pace < 240;

    const paceError = () => {
        if (toFast) return 'Your race time looks too fast, please check the time you entered.';
        if (tooSlow) return 'The training plan is not suitable for athletes racing over 20:00min/mi';
        return false;
    };

    if (paceError()) {
        const error = paceError();
        yield put(setRaceError({ index, key: 'timeError', error }));
    } else if (key === 'distance') {
        yield put(setRaceError({ index, key: 'timeError', error: null }));
    }

    const error = signupRaceValidator(value, key);
    if (error) {
        yield call(delay, 2000);
        yield put(setRaceError({
            index,
            key,
            error,
        }));
    }
}

export function* fieldChanged({ key }) {
    if (key !== 'milesweek') {
        return;
    }
    const { signup: { fields } } = yield select();
    const { milesweek } = fields;

    if (milesweek !== '' && Number(milesweek) === 0) {
        yield put(change('runday', 0));
        yield put(change('lrweek', 0));
    }
}

export function* activePlanChanged({ result }) {
    const activePlan = result['Active Plan'] === 'Yes';
    const oldPlan = result['Old Plan'] === 'Yes';
    const planOver = result['Plan Over'] === 'Yes';
    const { auth: { runnerId } } = yield select();
    if (runnerId) { // if logged in
        if (activePlan) {
            if (planOver) {
                return null;
            }
            yield put(setError({
                key: 'email',
                error: 'You have an active training plan.  Please contact RUNtrix to start a new one.',
            }));
            yield put(openActivePlanAlert());
        }
    } else if (activePlan || oldPlan) {
        yield put(setError({
            key: 'email',
            error: 'An account with that email already exists. ' +
                'Please enter different one.',
        }));
        yield put(openIsYouModal());
    }
    return null;
}

export function* nycRacesFetched({ result, paramsRace }) {
    const { theme: { themeId } } = yield select();
    const urlRace = result.filter(race => race.race_abbr === paramsRace)[0];

    // Custom stuff for NYRR
    if (paramsRace) {
        yield put(changeAllFields({
            raceName: urlRace.race_name,
            raceDate: urlRace.race_date && moment(urlRace.race_date).toDate(),
            distance: +urlRace.race_dist,
            tweek: Number(urlRace.race_dist) === 13.1 ? 10 : null,
            terrain: urlRace.race_terrain &&
            findKey(terrains, partial(isEqual, urlRace.race_terrain)),
        }));
    } else if (themeId === '1') {
        const nyc = result.filter(race => race.race_name === 'TCS New York City Marathon')[0];
        yield put(changeAllFields({
            raceName: nyc.race_name,
            raceDate: nyc.race_date && moment(nyc.race_date).toDate(),
            distance: +nyc.race_dist,
            terrain: nyc.race_terrain && findKey(terrains, partial(isEqual, nyc.race_terrain)),
        }));
    }
}

export function* errorCheckAllFields() {
    const { signup: { fields } } = yield select();
    yield Object.entries(fields).forEach((field) => {
        const key = field[0];
        const val = field[1];
        put(setErrorListener({ val, key }));
    });
}

export function* retriggerConfirmPurchase({ result, passthroughObj }) {
    if (result) {
        if (result.webhookHit === 0) {
            try {
                const { signup: { purchase: { retryPurchaseTried } } } = yield select();
                if (!retryPurchaseTried) {
                    yield delay(2000);
                    yield put({ type: 'signup/RETRY_CONFIRM_PURCHASE' });
                    yield put(confirmPurchaseCompleted(passthroughObj));
                }
            } catch (error) {
                yield put({ type: 'signup/RETRY_CONFIRM_PURCHASE_FAILED', error });
            }
        } else {
            try {
                const { signup: { fields: { email, password } } } = yield select();
                const userinfo = { email, password };
                const failedPurchase = true;
                yield put({ type: 'signup/SHOW_PURCHASE_SPINNER' });
                yield delay(3000);
                yield put(loginAction(userinfo, failedPurchase));
            } catch (error) {
                yield put({ type: 'signup/PROCESS_ORDER_FAILED' });
            }
        }
    } else {
        yield put({ type: 'signup/PROCESS_ORDER_FAILED' });
    }
}

export function* watchFieldChangeForError() {
    yield takeLatestFilter(CHANGE_FIELD, ({ key }) => key, setErrorListener);
}

export function* watchFieldChange() {
    yield takeEvery(CHANGE_FIELD, fieldChanged);
}


export function* watchRaceChange() {
    yield takeLatestFilter(CHANGE_RACE, ({ index, key }) => [index, key].join('-'), setRaceErrorListener);
}

export function* watchActiveChange() {
    yield takeEvery(GET_ACTIVE_PLAN_SUCCESS, activePlanChanged);
}

export function* watchNycRacesFatch() {
    yield takeEvery(FETCH_NYRR_RACES_SUCCESS, nycRacesFetched);
}

export function* watchFetchLoggedInSignup() {
    yield takeEvery(GET_SIGNUP_LOGGED_IN_SUCCESS, errorCheckAllFields);
}

export function* watchConfirmPurchaseFailure() {
    yield takeEvery(CONFIRM_PLAN_PURCHASE_FAILURE, retriggerConfirmPurchase);
}

export function* rootSignupSaga() {
    yield [
        watchRaceChange(),
        watchFieldChange(),
        watchFieldChangeForError(),
        watchActiveChange(),
        watchFetchLoggedInSignup(),
        watchNycRacesFatch(),
        watchConfirmPurchaseFailure(),
    ];
}

// -------
// ACTIONS
// -------
export function change(key, val) {
    return {
        type: CHANGE_FIELD,
        key,
        val,
    };
}

export function setPlanId(planId) {
    return {
        type: SET_PLAN_ID,
        planId,
    };
}

export function setRunnerId(runnerId) {
    return {
        type: SET_RUNNER_ID,
        runnerId,
    };
}


export function changeCharityCode(key, val) {
    return {
        type: CHANGE_CHARITY_CODE,
        key,
        val,
    };
}

export function changeAllFields(race) {
    return {
        type: CHANGE_ALL_FIELDS,
        race,
    };
}

export function setError({ key, error }) {
    return {
        type: SET_ERROR,
        key,
        error,
    };
}


export function clearError(key) {
    return {
        type: CLEAR_ERROR,
        key,
    };
}

export function setRaceError({ index, key, error }) {
    return {
        type: SET_RACE_ERROR,
        key,
        error,
        index,
    };
}

export function addRace() {
    return {
        type: ADD_RACE,
    };
}

export function removeRace(index) {
    return {
        type: REMOVE_RACE,
        index,
    };
}

export function changeRace(index, key, value) {
    return {
        type: CHANGE_RACE,
        index,
        key,
        value,
    };
}

export const openIsYouModal = () => ({
    type: OPEN_IS_YOU_MODAL,
});

export const closeIsYouModal = () => ({
    type: CLOSE_IS_YOU_MODAL,
});

export const paddleClose = () => ({
    type: CLOSE_PADDLE,
});

export const paddleOpen = () => ({
    type: OPEN_PADDLE,
});

export const openActivePlanAlert = () => ({
    type: OPEN_ACTIVE_PLAN_ALERT,
});

export const closeActivePlanAlert = () => ({
    type: CLOSE_ACTIVE_PLAN_ALERT,
});

export const processOrderFailed = () => ({
    type: PROCESS_ORDER_FAILED,
});

export const resetFillingOrder = () => ({
    type: RESET_FILLING_ORDER,
});

export function fetchSignupFieldsLoggedIn(body = {}) {
    return {
        types: [GET_SIGNUP_LOGGED_IN, GET_SIGNUP_LOGGED_IN_SUCCESS, GET_SIGNUP_LOGGED_IN_FAILURE],
        promise: () => fetch(`${process.env.RTAPI_URL}/API_SignUp.php`, {
            method: 'POST',
            body: prepareBody({
                runner_id: body.runnerId,
            }),
        }).then(res => res.json()),
    };
}

export function fetchPaddleProducts(themeId) {
    return {
        types: [FETCH_PADDLE_PRODUCTS, FETCH_PADDLE_PRODUCTS_SUCCESS, FETCH_PADDLE_PRODUCTS_FAILURE], //eslint-disable-line
        promise: () => fetch(`${process.env.RT_PADDLE_URL}/API_paddle_products.php`, {
            method: 'POST',
            body: prepareBody({
                theme_id: themeId,
            }),
        }).then(res => res.json()),
        themeId,
    };
}

export function confirmPurchaseCompleted(passthroughObj) {
    return {
        types: [CONFIRM_PLAN_PURCHASE, CONFIRM_PLAN_PURCHASE_SUCCESS, CONFIRM_PLAN_PURCHASE_FAILURE], //eslint-disable-line
        promise: () => fetch(`${process.env.RT_PADDLE_URL}/API_confirm_purchase.php`, {
            method: 'POST',
            body: prepareBody({
                ...passthroughObj,
            }),
        }).then(res => res.json()),
        passthroughObj,
    };
}


export function fetchLevels(fields, races) {
    const body = mapFieldsForLevels(fields, races);
    return {
        types: [FETCH_LEVELS, FETCH_LEVELS_SUCCESS, FETCH_LEVELS_FAILURE],
        promise: ({ getState }) => fetch(`${process.env.RTAPI_URL}/API_available_plans.php`, {
            method: 'POST',
            body: prepareBody({
                theme_id: selectThemeId(getState()),
                ...body,
            }),
        }).then(res => res.json()),
    };
}

export function fetchCharities(themeId) {
    return {
        types: [FETCH_CHARITIES, FETCH_CHARITIES_SUCCESS, FETCH_CHARITIES_FAILURE],
        promise: () => fetch(`${process.env.RTAPI_URL}/API_charities.php`, {
            method: 'POST',
            body: prepareBody({
                theme_id: themeId,
            }),
        }).then(res => res.json()),
    };
}

export function checkCharity(body = {}) {
    return {
        types: [CHECK_CHARITY_CODE, CHECK_CHARITY_CODE_SUCCESS, CHECK_CHARITY_CODE_FAILURE],
        promise: ({ getState }) => fetch(`${process.env.RTAPI_URL}/API_charities_validation.php`, {
            method: 'POST',
            body: prepareBody({
                theme_id: selectThemeId(getState()),
                ...body,
            }),
        }).then(res => res.json()),
    };
}

export function getActivePlan(body = {}) {
    return {
        types: [GET_ACTIVE_PLAN, GET_ACTIVE_PLAN_SUCCESS, GET_ACTIVE_PLAN_FAILURE],
        promise: () => fetch(`${process.env.RTAPI_URL}/API_active_plan.php`, {
            method: 'POST',
            body: prepareBody({
                remail: body.email,
                goalracedist: body.goalracedist,
            }),
        }).then(res => res.json()),
        email: body.email,
    };
}

export function signupAction(options, { fields, races }) {
    const body = mapFieldsForSignup({
        ...fields,
        ...options,
    }, races);
    return {
        types: [SIGNUP, SIGNUP_SUCCESS, SIGNUP_FAILURE],
        promise: ({ getState }) => fetch(`${process.env.RTAPI_URL}/API_create_plan.php`, {
            method: 'POST',
            body: prepareBody({
                theme_id: selectThemeId(getState()),
                ...body,
            }),
        }).then(res => res.json()),
        fields,
        races,
        ...options,
    };
}

export const stripeCheckout = body => ({
    types: [STRIPE_CHECKOUT, STRIPE_CHECKOUT_SUCCESS, STRIPE_CHECKOUT_FAILURE],
    promise: ({ getState }) => fetch(`${process.env.RT_STRIPE_URL}/API_epirts_2021.php`, {
        method: 'POST',
        body: prepareBody({
            theme_id: selectThemeId(getState()),
            ...body,
        }),
    }).then(res => res.json()),
    planId: body.plan_id,
});

export const completeFreeCharge = body => ({
    types: [FREE_CHARGE, FREE_CHARGE_SUCCESS, FREE_CHARGE_FAILURE],
    promise: ({ getState }) => fetch(`${process.env.RT_STRIPE_URL}/API_freecharge.php`, {
        method: 'POST',
        body: prepareBody({
            theme_id: selectThemeId(getState()),
            ...body,
        }),
    }).then(res => res.json()),
});

export const checkPayment = (body = {}) => ({
    types: [CHECK_PAYMENT, CHECK_PAYMENT_SUCCESS, CHECK_PAYMENT_FAILURE],
    promise: () => fetch(`${process.env.RT_STRIPE_URL}/API_epirts_check.php`, {
        method: 'POST',
        body: prepareBody({
            ...body,
        }),
    }).then(res => res.json()),
    planId: body.planId,
});

export const stripeChargeGC = body => ({
    types: [STRIPE_CHARGE_GC, STRIPE_CHARGE_GC_SUCCESS, STRIPE_CHARGE_GC_FAILURE],
    promise: ({ getState }) => fetch(`${process.env.RT_STRIPE_URL}/API_epirts_1_gc.php`, {
        method: 'POST',
        body: prepareBody({
            theme_id: selectThemeId(getState()),
            ...body,
        }),
    }).then(res => res.json()),
    planId: body.plan_id,
});


// ---------
// SELECTORS
// ---------
export function mapFieldsForLevels(fields, races) {
    const english = fields.units === 'english';
    const passUnits = fields.units === 'english' ? 'English' : 'Metric';
    const raceInfo = getRaceInfo(fields, races);
    const data = {
        rgender: fields.gender,
        rage: fields.rage,
        height: Math.floor(fields.height / 12),
        inches: fields.height % 12,
        rmotiv: fields.motivation, // motivation from constants
        milesweekX: english ?
            fields.milesweek : kmToMile(fields.milesweek),

        yearsrun: fields.yearsrun,
        tweek: fields.tweek,
        lrweek: fields.lrweek,
        runpace: fields.runpace,
        runday: fields.runday,
        goalracedistance: fields.distance, // miles; 9.3, 13.1 or 26.2,
        injury: fields.injury,
        units: passUnits,
        ...raceInfo,
    };

    return data;
}

const getRaceInfo = (fields, races) => {
    const data = {};
    if (fields.runRace && races) {
        // racerace1 → pre-plan race #1 distance (meters)
        // racehr1 → pre-plan race #1 time (hours, integer)
        // racemin1 → pre-plan race #1 time (minutes, integer)
        // racesec1 → pre-plan race #1 time (seconds, integer)
        // racerace2 → pre-plan race #2 distance (meters)
        // racehr2 → pre-plan race #2 time (hours, integer)
        // racemin2 → pre-plan race #2 time (minutes, integer)
        // racesec2 → pre-plan race #2 time (seconds, integer)
        // racerace3 → pre-plan race #3  distance (meters)
        // racehr3 → pre-plan race #3  time (hours, integer)
        // racemin3 → pre-plan race #3  time (minutes, integer)
        // racesec3 → pre-plan race #3 time (seconds, integer)

        races.forEach((race, index) => {
            if (isSet(race.distance)) {
                data[`racerace${index + 1}`] = race.distance;
                data[`racehr${index + 1}`] = race.hours || 0;
                data[`racemin${index + 1}`] = race.minutes || 0;
                data[`racesec${index + 1}`] = race.seconds || 0;
            }
        });
    }
    return data;
};

export function mapFieldsForSignup(fields, races) {
    const raceInfo = getRaceInfo(fields, races);
    const data = {
        rname: `${fields.fname} ${fields.lname}`, // → Name of Runner
        rgender: fields.gender, // → male or female
        rage: fields.rage, // → age of runner (integer)
        remail: fields.email, // → email of runner
        rlevel: fields.knowledge, // → running knowledge (integer)
        goalraceterrain: fields.terrain,
        // → distance of goal race (float; 9.3, 13.1, 26.2)
        goalracedistance: fields.distance,
        goalracename: fields.raceName, // → name of goal race
        goalracedate: moment(fields.raceDate).format('YYYY-MM-DD'), // → date of goal race  (e.x. 2017-03-18)
        milesweek: fields.milesweek, // → miles per week (miles, float)
        lrweek: fields.lrweek, // → typical long run per week (minutes, integer)
        runpace: fields.runpace, // → average running pace (minutes, float) (e.x. 8.05)
        runday: fields.runday, // → # of running days (integer)
        ...raceInfo,
        yearsrun: fields.yearsrun, // → integer (years of running experience)
        comara: fields.runMarathon ? 'Yes' : 'No', // → Yes, No (marathon or half marathon experience)
        mgoal: fields.mgoal, // → Conservative, Moderate or Advanced
        mgoaltime1: fields.mgoaltime1, // → marathon goal time #1 (e.x. 4:07:11)
        mgoaltime2: fields.mgoaltime2, // → marathon goal time #2 (e.x. 4:07:11)
        mgoaltime3: fields.mgoaltime3, // → marathon goal time #3 (e.x. 4:07:11)
        mgoaltime4: fields.mgoaltime4, // → marathon goal time #4 (e.x. 4:07:11)
        rmotiv: fields.motivation, // → integer
        tweek: fields.tweek,
        injury: fields.injury, // → integer
        rzip: fields.zipcode, // → 5-digit integer
        height_total: fields.height, // → integer (total iinches)
        weight: fields.weight, // → integer (lbs)
        units: fields.units === 'metric' ? 'Metric' : 'English', // → Metric or English
        dorwssap: fields.password, // → password
        extra: fields.extra,
        slowrace: fields.slowrace,
    };

    return data;
}

export const filterAvailablePlans = items => (
    items ? items.filter(plan =>
        plan.options && plan.options['Weekly Mileage'],
    ) : null
);

export const getPaddleProductId = (productList, ecoach, tweek, distance, upgrade, freeplan) => {
    if (upgrade) {
        const runtrixProductId = `ecoachupgrade_${tweek}wk`;
        return productList[runtrixProductId].paddleId;
    }
    const ecoachString = ecoach ? 'ecoach' : 'noecoach';
    const freePlanString = freeplan ? '_freeplan' : '';
    const runtrixProductId = `${ecoachString}_${distance}_${tweek}${freePlanString}`;
    return productList[runtrixProductId].paddleId;
};




/** WEBPACK FOOTER **
 ** ./src/redux/signup.js
 **/